import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, GripVertical, Trash2, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { equipmentService } from '../../services/equipment';
import EquipmentTypePicker from '../../components/equipment/EquipmentTypePicker';
import EquipmentTypesManager from '../../components/equipment/EquipmentTypesManager';
import { cn } from '../../utils/cn';
import type { ChecklistTemplate, ChecklistTemplateItem } from '../../types/equipment';

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

// ─── Types ────────────────────────────────────────────────────────────────────

interface DraftItem {
  id?: number;
  tempId: string;
  item_text: string;
  is_required: boolean;
  sort_order: number;
}

interface TemplateDraft {
  name: string;
  equipment_type: string;
  custom_type_label: string;
  description: string;
  items: DraftItem[];
}

const EMPTY_DRAFT: TemplateDraft = {
  name: '',
  equipment_type: '',
  custom_type_label: '',
  description: '',
  items: [],
};

// ─── Checklist Templates ──────────────────────────────────────────────────────

const TABS = [
  { key: 'checklists', label: 'Checklists' },
  { key: 'types',      label: 'Equipment types' },
] as const;

export default function EquipmentAdminSettings() {
  const navigate = useNavigate();
  // In the URL so a refresh, or a link from elsewhere, lands on the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'types' ? 'types' : 'checklists';

  const [templates, setTemplates]   = useState<ChecklistTemplate[]>([]);
  const [loading, setLoading]       = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editingId, setEditingId]   = useState<number | 'new' | null>(null);
  const [draft, setDraft]           = useState<TemplateDraft>(EMPTY_DRAFT);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [pageError, setPageError]   = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await equipmentService.listChecklistTemplates();
      if (res.success && res.data) setTemplates(res.data);
      setPageError(null);
    } catch (err) {
      setPageError(errorMessage(err, 'Could not load checklist templates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setDraft(EMPTY_DRAFT);
    setEditingId('new');
    setError(null);
  };

  const openEdit = (tmpl: ChecklistTemplate) => {
    setDraft({
      name: tmpl.name,
      equipment_type: tmpl.equipment_type || '',
      custom_type_label: (tmpl as any).custom_type_label || '',
      description: tmpl.description || '',
      items: (tmpl.items || []).map((item: ChecklistTemplateItem, idx: number) => ({
        id: item.id,
        tempId: String(item.id),
        item_text: item.item_text,
        is_required: item.is_required,
        sort_order: item.sort_order ?? idx,
      })),
    });
    setEditingId(tmpl.id);
    setError(null);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deactivate this checklist template?')) return;
    try {
      await equipmentService.deleteChecklistTemplate(id);
      load();
    } catch (err) {
      setPageError(errorMessage(err, 'Could not deactivate the template'));
    }
  };

  const addItem = () => {
    const tempId = `new-${Date.now()}`;
    setDraft(prev => ({
      ...prev,
      items: [...prev.items, { tempId, item_text: '', is_required: true, sort_order: prev.items.length }],
    }));
  };

  const removeItem = (tempId: string) => {
    setDraft(prev => ({
      ...prev,
      items: prev.items.filter(i => i.tempId !== tempId).map((i, idx) => ({ ...i, sort_order: idx })),
    }));
  };

  const updateItem = (tempId: string, field: 'item_text' | 'is_required', value: string | boolean) => {
    setDraft(prev => ({
      ...prev,
      items: prev.items.map(i => i.tempId === tempId ? { ...i, [field]: value } : i),
    }));
  };

  const handleSave = async () => {
    if (!draft.name.trim()) { setError('Template name is required'); return; }
    if (draft.items.length === 0) { setError('Add at least one checklist item'); return; }
    if (draft.items.some(i => !i.item_text.trim())) { setError('All items must have text'); return; }
    setSaving(true);
    setError(null);

    const payload = {
      name: draft.name.trim(),
      equipment_type: draft.equipment_type || null,
      description: draft.description || undefined,
      items: draft.items.map((i, idx) => ({
        item_text: i.item_text.trim(),
        is_required: i.is_required,
        sort_order: idx,
      })),
    };

    // apiService rejects on every failure — a dropped connection as much as a
    // 400 — so without the catch the button sits on "Saving" with no message.
    try {
      const res = editingId === 'new'
        ? await equipmentService.createChecklistTemplate(payload)
        : await equipmentService.updateChecklistTemplate(editingId as number, payload);
      if (res.success) {
        setEditingId(null);
        load();
      } else {
        setError(res.error?.message || 'Save failed');
      }
    } catch (err) {
      setError(errorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Equipment Settings"
        subtitle={tab === 'types' ? 'The types equipment is registered under' : 'Pre-start checklist templates'}
        icon={<Settings className="h-6 w-6 text-white" />}
        actionButtons={tab === 'checklists' ? (
          <Button size="sm" onClick={openNew}>
            <Plus className="h-4 w-4 mr-1" />
            Add Template
          </Button>
        ) : undefined}
      />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        <div className="flex flex-wrap gap-1.5 text-sm" role="tablist" aria-label="Equipment settings">
          {TABS.map(t => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setSearchParams(t.key === 'checklists' ? {} : { tab: t.key }, { replace: true })}
              className={cn('px-3 py-1.5 rounded-lg border font-medium transition-colors',
                tab === t.key
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50')}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'types' ? <EquipmentTypesManager /> : (<>

        {pageError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{pageError}</p>
        )}

        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-white rounded-xl border border-neutral-100 animate-pulse" />)}
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-neutral-500 mb-1">No checklist templates yet</p>
            <p className="text-sm text-neutral-400 mb-4">
              Create templates to use in pre-start checks. You can scope them to a specific equipment type.
            </p>
            <Button onClick={openNew}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add First Template
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map(tmpl => (
              <div key={tmpl.id} className={`border rounded-lg overflow-hidden ${tmpl.is_active ? 'border-neutral-200' : 'border-neutral-100 opacity-60'}`}>
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      onClick={() => setExpandedId(prev => prev === tmpl.id ? null : tmpl.id)}
                      className="text-neutral-400 hover:text-neutral-600 flex-shrink-0"
                    >
                      {expandedId === tmpl.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-800 truncate">{tmpl.name}</p>
                      <p className="text-xs text-neutral-400">
                        {tmpl.equipment_type
                          ? (tmpl.equipment_type === 'custom'
                              ? ((tmpl as any).custom_type_label || 'Custom type')
                              : tmpl.equipment_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                          : 'All equipment'}
                        {' · '}{tmpl.items?.length ?? 0} item{(tmpl.items?.length ?? 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${tmpl.is_active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>
                      {tmpl.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={() => openEdit(tmpl)}
                      className="text-xs text-neutral-400 hover:text-green-600 px-2 py-1 rounded hover:bg-green-50 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(tmpl.id)}
                      className="text-neutral-300 hover:text-red-500 p-1 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {expandedId === tmpl.id && tmpl.items && tmpl.items.length > 0 && (
                  <div className="divide-y divide-neutral-100 bg-white">
                    {[...tmpl.items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((item, idx) => (
                      <div key={item.id} className="flex items-start gap-3 px-4 py-2.5">
                        <span className="text-xs text-neutral-300 flex-shrink-0 mt-0.5 w-5 text-right">{idx + 1}.</span>
                        <p className="text-sm text-neutral-700 flex-1">{item.item_text}</p>
                        {item.is_required && <span className="text-xs text-red-400 flex-shrink-0">Required</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>)}

        <Button variant="outline" className="w-full" onClick={() => navigate('/equipment')}>
          Back to Equipment
        </Button>
      </div>

      {/* Edit / New modal */}
      {editingId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full my-4">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-neutral-800">
                {editingId === 'new' ? 'New Checklist Template' : 'Edit Checklist Template'}
              </h3>
              <button onClick={() => setEditingId(null)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-neutral-600 mb-1">Template name *</label>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={e => setDraft(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Daily Vehicle Check"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 mb-1">
                    Equipment type
                    <span className="font-normal text-neutral-400 ml-1">(blank = all)</span>
                  </label>
                  <EquipmentTypePicker
                    value={draft.equipment_type}
                    customLabel={draft.custom_type_label}
                    onChange={(typeKey, label) =>
                      setDraft(prev => ({ ...prev, equipment_type: typeKey, custom_type_label: label }))
                    }
                  />
                  {draft.equipment_type && (
                    <button
                      type="button"
                      onClick={() => setDraft(prev => ({ ...prev, equipment_type: '', custom_type_label: '' }))}
                      className="text-xs text-neutral-400 hover:text-red-500 mt-1"
                    >
                      × Clear (apply to all equipment)
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Description</label>
                <input
                  type="text"
                  value={draft.description}
                  onChange={e => setDraft(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional instructions for operators"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-neutral-600">Checklist items *</label>
                  <button
                    onClick={addItem}
                    className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add item
                  </button>
                </div>
                {draft.items.length === 0 ? (
                  <p className="text-xs text-neutral-400 text-center py-4 border border-dashed border-neutral-200 rounded-lg">
                    No items yet — click "Add item" to start
                  </p>
                ) : (
                  <div className="space-y-2">
                    {draft.items.map((item, idx) => (
                      <div key={item.tempId} className="flex items-start gap-2 p-2 bg-neutral-50 rounded-lg border border-neutral-200">
                        <GripVertical className="h-4 w-4 text-neutral-300 flex-shrink-0 mt-2" />
                        <span className="text-xs text-neutral-400 flex-shrink-0 mt-2.5 w-4">{idx + 1}.</span>
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <input
                            type="text"
                            value={item.item_text}
                            onChange={e => updateItem(item.tempId, 'item_text', e.target.value)}
                            placeholder="e.g. Check tyre pressures"
                            className="w-full border border-neutral-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={item.is_required}
                              onChange={e => updateItem(item.tempId, 'is_required', e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-xs text-neutral-600">Required (must pass)</span>
                          </label>
                        </div>
                        <button
                          onClick={() => removeItem(item.tempId)}
                          className="text-neutral-300 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editingId === 'new' ? 'Create Template' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
